import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CircularProgress,
  Alert,
  Box,
  Chip,
  IconButton,
  Tooltip,
  Button,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  PostAdd as PostAddIcon,
  OpenInNew as OpenInNewIcon,
  Inventory as InventoryIcon,
  Receipt as LiquidacionIcon,
} from '@mui/icons-material';
import { type GridColDef, type GridRenderCellParams } from '@mui/x-data-grid';
import { DataGrid, PageTitle, GridEditButton } from '../../../components/common';
import { PermissionGuard } from '../../../auth/components/PermissionGuard';
import { useGuiaProduccionStore } from './store/useGuiaProduccionStore';
import type { EstadoGuiaProduccion } from './types';

const estadoLabels: Record<EstadoGuiaProduccion, string> = {
  'PEN': 'Pendiente',
  'APR': 'Aprobado',
  'ANU': 'Anulado',
};

const estadoColors: Record<EstadoGuiaProduccion, 'warning' | 'success' | 'error'> = {
  'PEN': 'warning',
  'APR': 'success',
  'ANU': 'error',
};

const GuiasProduccionPage: React.FC = () => {
  const navigate = useNavigate();
  const [selectedTab, setSelectedTab] = useState(0);

  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const {
    guias,
    loading,
    error,
    fetchGuias,
    clearError
  } = useGuiaProduccionStore();

  // Filter guias by tab
  // When Parte (Paso 3) is APR, the guía moves from APROBADAS to LISTA
  const filteredGuias = guias.filter((guia) => {
    switch (selectedTab) {
      case 0: // Pendientes
        return guia.estado === 'PEN';
      case 1: // Aprobadas (excluir si su Parte está aprobado)
        return guia.estado === 'APR' && guia.estadoParte !== 'APR';
      case 2: // Anuladas
        return guia.estado === 'ANU';
      case 3: // Lista (todos)
        return true;
      default:
        return true;
    }
  });

  // Para mostrar solo un botón "Generar Parte" por DERC en el listado
  // Solo si TODAS las guías del mismo DERC están aprobadas
  const getFirstGuiaIdByDerc = (): Set<number> => {
    const firstGuiaIds = new Set<number>();

    // Agrupar guías por DERC (excluir anuladas, no deben bloquear el flujo)
    const guiasByDerc = new Map<string, typeof guias>();
    for (const guia of guias) {
      if (!guia.numeroDocumento) continue;
      if (guia.estado === 'ANU') continue;
      const existing = guiasByDerc.get(guia.numeroDocumento) || [];
      existing.push(guia);
      guiasByDerc.set(guia.numeroDocumento, existing);
    }

    // Para cada DERC, verificar que TODAS las guías activas estén aprobadas
    for (const [, guiasDerc] of guiasByDerc) {
      const todasAprobadas = guiasDerc.every(g => g.estado === 'APR');
      const algunaSinParte = guiasDerc.some(g => !g.tieneParte);

      if (todasAprobadas && algunaSinParte) {
        // Mostrar botón solo en la primera guía sin parte (ordenada por ID)
        const primeraGuiaSinParte = [...guiasDerc]
          .filter(g => !g.tieneParte)
          .sort((a, b) => a.id - b.id)[0];
        if (primeraGuiaSinParte) {
          firstGuiaIds.add(primeraGuiaSinParte.id);
        }
      }
    }

    return firstGuiaIds;
  };

  const firstGuiaIdsForParte = getFirstGuiaIdByDerc();

  const handleNavigateToGuiaMateriaPrima = (idGuiaMateriaPrima: number) => {
    navigate(`/produccion/guias-materia-prima/${idGuiaMateriaPrima}`);
  };

  const columns: GridColDef[] = [
    {
      field: 'id',
      headerName: '#',
      width: 70,
      type: 'number'
    },
    {
      field: 'numeroGuiaMateriaPrima',
      headerName: 'Guía MP',
      width: 90,
      renderCell: (params: GridRenderCellParams) => {
        const idGuiaMateriaPrima = params.row.idGuiaMateriaPrima;
        const numeroGuia = params.value;

        if (!idGuiaMateriaPrima) return '-';

        return (
          <Tooltip title="Ver Guía de Materia Prima">
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                cursor: 'pointer',
                color: 'primary.main',
                '&:hover': {
                  textDecoration: 'underline'
                }
              }}
              onClick={(e) => {
                e.stopPropagation();
                handleNavigateToGuiaMateriaPrima(idGuiaMateriaPrima);
              }}
            >
              {numeroGuia || idGuiaMateriaPrima}
              <OpenInNewIcon sx={{ fontSize: 14 }} />
            </Box>
          </Tooltip>
        );
      },
    },
    {
      field: 'idOrigenProducto',
      headerName: 'Tipo',
      width: 50,
      renderCell: (params: GridRenderCellParams) => {
        const tipo = params.value === 1 ? 'A' : params.value === 2 ? 'B' : '-';
        return (
          <Chip
            label={tipo}
            size="small"
            color={params.value === 1 ? 'primary' : 'error'}
            variant="filled"
          />
        );
      },
    },
    {
      field: 'numeroDocumento',
      headerName: 'N° DERC',
      width: 130,
    },
    {
      field: 'fechaExtraccion',
      headerName: 'Fecha',
      width: 90,
      type: 'date',
      valueFormatter: (value) => {
        if (!value) return '';
        return new Date(value).toLocaleDateString('es-ES');
      }
    },
    {
      field: 'proveedor',
      headerName: 'Proveedor',
      width: 120,
      flex: 1,
      minWidth: 100
    },
    {
      field: 'asociacion',
      headerName: 'Asociación',
      width: 100,
      renderCell: (params: GridRenderCellParams) => {
        const isTypeB = params.row.idOrigenProducto === 2;
        if (isTypeB) return 'N/A';
        return params.value || '';
      },
    },
    {
      field: 'duenio',
      headerName: 'Dueño',
      width: 110
    },
    {
      field: 'encargado',
      headerName: 'Encargado',
      width: 110
    },
    {
      field: 'numeroMallas',
      headerName: 'Mallas',
      width: 60,
      type: 'number'
    },
    {
      field: 'subLoteCantidad',
      headerName: 'Sub Lote',
      width: 75,
      type: 'number',
      renderCell: (params: GridRenderCellParams) => {
        // Only show Sub Lote for Type A (idOrigenProducto === 1)
        const isTypeA = params.row.idOrigenProducto === 1;
        if (!isTypeA || params.value == null) return '';
        return (
          <Chip
            label={params.value}
            size="small"
            color="primary"
            variant="outlined"
          />
        );
      },
    },
    {
      field: 'numeroParte',
      headerName: 'N° Parte',
      width: 90,
      renderCell: (params: GridRenderCellParams) => {
        const idParte = params.row.idParte;
        const numeroParte = params.value;

        if (!idParte) return '-';

        return (
          <Tooltip title="Ver Parte de Producción">
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                cursor: 'pointer',
                color: 'secondary.main',
                '&:hover': {
                  textDecoration: 'underline'
                }
              }}
              onClick={(e) => {
                e.stopPropagation();
                handleViewParte(idParte);
              }}
            >
              {numeroParte || idParte}
              <OpenInNewIcon sx={{ fontSize: 14 }} />
            </Box>
          </Tooltip>
        );
      },
    },
    {
      field: 'estado',
      headerName: 'Estado',
      width: 110,
      renderCell: (params: GridRenderCellParams) => {
        const estado = (params.value as EstadoGuiaProduccion) || 'PEN';
        return (
          <Chip
            label={estadoLabels[estado]}
            color={estadoColors[estado]}
            size="small"
          />
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Acciones',
      width: 150,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        const isPendiente = params.row.estado === 'PEN';
        const isAprobada = params.row.estado === 'APR';
        const isAnulada = params.row.estado === 'ANU';
        const tieneParte = params.row.tieneParte;
        // Solo mostrar botón "Generar Parte" en la primera guía de cada DERC (solo Tipo A)
        const showGenerarParte = firstGuiaIdsForParte.has(params.row.id) && params.row.idOrigenProducto !== 2;

        // Anuladas: sin acciones
        if (isAnulada) return null;

        return (
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {isPendiente ? (
              // Pendientes: Lápiz para editar
              <PermissionGuard permission="PRODUCCION_EDITAR">
              <GridEditButton
                onClick={() => handleEdit(params.row.id)}
                tooltip="Editar"
                fontSize="small"
                ariaLabel={`Editar guía ${params.row.id}`}
              />
              </PermissionGuard>
            ) : isAprobada ? (
              <>
                {/* Aprobadas: Ojo para ver/cambiar estado */}
                <Tooltip title="Ver / Cambiar estado">
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => handleEdit(params.row.id)}
                    aria-label={`Ver guía ${params.row.id}`}
                  >
                    <VisibilityIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                {/* Flecha para generar/ver parte */}
                {tieneParte ? (
                  <Tooltip title="Ver Parte de Producción">
                    <IconButton
                      size="small"
                      color="secondary"
                      onClick={() => handleViewParte(params.row.idParte)}
                      aria-label={`Ver parte de producción de guía ${params.row.id}`}
                    >
                      <PostAddIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                ) : showGenerarParte ? (
                  <PermissionGuard permission="PRODUCCION_EDITAR">
                  <Tooltip title="Generar Parte de Producción">
                    <IconButton
                      size="small"
                      color="success"
                      onClick={() => handleGenerarParte(params.row.id)}
                      aria-label={`Generar parte para guía ${params.row.id}`}
                    >
                      <PostAddIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  </PermissionGuard>
                ) : null}
                {/* Botón generar liquidación */}
                <PermissionGuard permission="LIQUIDACION_GENERAR">
                <Tooltip title="Generar Orden de Liquidación">
                  <IconButton
                    size="small"
                    color="warning"
                    onClick={() => navigate(`/administracion/orden-liquidacion/nueva?guiaId=${params.row.id}`)}
                    aria-label={`Generar liquidación para guía ${params.row.id}`}
                  >
                    <LiquidacionIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                </PermissionGuard>
              </>
            ) : null}
          </Box>
        );
      },
    },
  ];

  useEffect(() => {
    fetchGuias();
  }, [fetchGuias]);

  const _handleAdd = () => {
    navigate('/produccion/guias-produccion/new');
  };
  void _handleAdd; // Explicitly mark as intentionally unused

  const handleEdit = (id: number) => {
    navigate(`/produccion/guias-produccion/${id}`);
  };

  const handleViewParte = (idParte?: number) => {
    if (idParte) {
      navigate(`/produccion/partes-produccion/${idParte}?readonly=true`);
    }
  };

  const handleGenerarParte = (id: number) => {
    navigate(`/produccion/partes-produccion/new?guiaId=${id}`);
  };

  const handleCloseError = () => {
    clearError();
  };

  const handleGoToGuiasMateriaPrima = () => {
    navigate('/produccion/guias-materia-prima');
  };

  return (
    <Box sx={{ p: 2, width: '100%' }}>
      <PageTitle
        title="Guías de Producción"
        subtitle="Gestión de Guías de Producción"
        flowStep={2}
        actions={
          <Button
            variant="outlined"
            color="primary"
            startIcon={<InventoryIcon />}
            onClick={handleGoToGuiasMateriaPrima}
            size="small"
          >
            Guías Materia Prima
          </Button>
        }
      />

      <Box sx={{ width: '100%' }}>

        {successMsg && (
          <Alert severity="success" onClose={() => setSuccessMsg(null)} sx={{ mb: 2 }}>
            {successMsg}
          </Alert>
        )}

        {error && (
          <Alert
            severity="error"
            onClose={handleCloseError}
            sx={{ mb: 2 }}
          >
            {error}
          </Alert>
        )}

        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs
            value={selectedTab}
            onChange={(_e, newValue) => setSelectedTab(newValue)}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label={`Pendientes (${guias.filter(g => g.estado === 'PEN').length})`} />
            <Tab label={`Aprobadas (${guias.filter(g => g.estado === 'APR').length})`} />
            <Tab label={`Anuladas (${guias.filter(g => g.estado === 'ANU').length})`} />
            <Tab label={`Lista (${guias.length})`} />
          </Tabs>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <DataGrid
            rows={filteredGuias}
            columns={columns}
            loading={loading}
            getRowId={(row) => row.id}
            pageSize={25}
          />
        )}
      </Box>

    </Box>
  );
};

export default GuiasProduccionPage;